import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import MaintenanceRequestPage from './pages/MaintenanceRequestPage';
import UserProfilePage from './pages/UserProfilePage';
import AdminLoginPage from './pages/AdminLoginPage';
import WhatsAppWidget from './components/common/WhatsAppWidget';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import './assets/styles/rtl.scss';
// Admin imports
import AdminLayout from './components/admin/AdminLayout';
import DashboardPage from './components/admin/DashboardPage';
import PropertiesManagement from './components/admin/PropertiesManagement';
import IncomeDashboard from './components/admin/IncomeDashboard';
import UsersManagement from './components/admin/UsersManagement';
import ContactManagement from './components/admin/ContactManagement';
import HomeManagement from './components/admin/HomeManagement';
import AboutManagement from './components/admin/AboutManagement';
import MaintenanceManagement from './components/admin/MaintenanceManagement';
import WhatsappManagement from './components/admin/WhatsappManagement';

const App = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const location = useLocation();
  // Check if route is admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Admin authentication check
  const isAdminAuthenticated = () => {
    return localStorage.getItem('isAdmin') === 'true' && localStorage.getItem('adminToken');
  };
  
  // Apply admin-mode class to body when in admin routes
  useEffect(() => {
    if (isAdminRoute) {
      document.body.classList.add('admin-mode');
    } else {
      document.body.classList.remove('admin-mode');
    }
  }, [isAdminRoute]);
  
  useEffect(() => {
    // Fetch the active WhatsApp number
    const fetchWhatsappNumber = async () => {
      try {
        const response = await fetch(`/api/whatsapp`);
        const data = await response.json();
        
        if (data.success && data.data) {
          setWhatsappNumber(data.data.phone_number);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp number:', error);
      }
    };
    
    fetchWhatsappNumber();
  }, []);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Wrap admin component with AdminLayout and authentication check
  const withAdminLayout = (Component) => {
    return isAdminAuthenticated() ? (
      <AdminLayout>
        <Component />
      </AdminLayout>
    ) : (
      <Navigate to="/admin/login" />
    );
  };

  return (
    <AuthProvider>
      <LanguageProvider>
        <div className="app">
        {!isAdminRoute && <Header />}
        <main>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:id" element={<PropertyDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/maintenance-request" element={<MaintenanceRequestPage />} />
            <Route path="/profile" element={<UserProfilePage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={withAdminLayout(DashboardPage)} />
            <Route path="/admin/home" element={withAdminLayout(HomeManagement)} />
            <Route path="/admin/about" element={withAdminLayout(AboutManagement)} />
            <Route path="/admin/contact" element={withAdminLayout(ContactManagement)} />
            <Route path="/admin/properties" element={withAdminLayout(PropertiesManagement)} />
            <Route path="/admin/users" element={withAdminLayout(UsersManagement)} />
            <Route path="/admin/maintenance" element={withAdminLayout(MaintenanceManagement)} />
            <Route path="/admin/whatsapp" element={withAdminLayout(WhatsappManagement)} />
            <Route path="/admin/soldProperties" element={withAdminLayout(PropertiesManagement)} />
            <Route path="/admin/availableProperties" element={withAdminLayout(PropertiesManagement)} />
            <Route path="/admin/income" element={withAdminLayout(IncomeDashboard)} />
            <Route path="/admin/analytics" element={withAdminLayout(DashboardPage)} />
          </Routes>
        </main>
        {!isAdminRoute && (
          <>
            <Footer />
            {showScrollTop && (
              <button 
                className="scroll-to-top"
                onClick={scrollToTop}
                aria-label="Scroll to top"
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </button>
            )}
            <WhatsAppWidget phoneNumber={whatsappNumber} />
          </>
        )}
        </div>
      </LanguageProvider>
    </AuthProvider>
  );
};

export default App;
